import React, { useState } from 'react';
import axios from 'axios';
import { X, Loader2, Sparkles, Link2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const XLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
);

export const TweetImportButton = ({ onImported }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid="import-tweet-open-btn" onClick={() => setOpen(true)} className="px-4 py-2 bg-black hover:bg-gray-800 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2">
        <XLogo className="w-4 h-4" /> استيراد خبر من X
      </button>
      {open && <TweetImportModal onClose={() => setOpen(false)} onImported={onImported} />}
    </>
  );
};

const TweetImportModal = ({ onClose, onImported }) => {
  const [url, setUrl] = useState('');
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchTweet = async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API}/news/import-tweet`, { url }, { headers });
      setDraft(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'تعذر جلب التغريدة');
    } finally { setLoading(false); }
  };

  const publish = async (published) => {
    setSaving(true);
    try {
      await axios.post(`${API}/news`, {
        title: draft.title, summary: draft.summary, content: draft.content,
        image_file_id: draft.image_file_id, images: [], category: 'general',
        published, tags: ['X'], source: 'twitter', source_url: draft.source_url,
      }, { headers });
      onImported?.();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'فشل حفظ الخبر');
    } finally { setSaving(false); }
  };

  const field = (key, label, rows) => (
    <label className="block">
      <span className="text-xs font-bold text-gray-500">{label}</span>
      {rows ? (
        <textarea data-testid={`import-tweet-${key}-input`} rows={rows} value={draft[key] || ''} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-200 p-3 text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none" />
      ) : (
        <input data-testid={`import-tweet-${key}-input`} value={draft[key] || ''} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-200 p-3 text-sm font-bold focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none" />
      )}
    </label>
  );

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" dir="rtl" onClick={onClose}>
      <div data-testid="import-tweet-modal" className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-black text-white flex items-center justify-center"><XLogo className="w-5 h-5" /></div>
            <div>
              <h3 className="font-bold text-gray-900">تحويل تغريدة إلى خبر</h3>
              <p className="text-xs text-gray-500">الصق رابط التغريدة وسيصيغها الذكاء الاصطناعي كخبر جاهز</p>
            </div>
          </div>
          <button data-testid="import-tweet-close-btn" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Link2 className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
              <input data-testid="import-tweet-url-input" dir="ltr" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://x.com/alamjad_club/status/..." className="w-full rounded-lg border border-gray-200 py-3 pr-9 pl-3 text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none" />
            </div>
            <button data-testid="import-tweet-fetch-btn" onClick={fetchTweet} disabled={!url || loading} className="px-5 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-bold flex items-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {loading ? 'جاري الصياغة...' : 'جلب وصياغة'}
            </button>
          </div>
          {error && <div data-testid="import-tweet-error" className="rounded-lg bg-red-50 text-red-700 text-sm p-3">{error}</div>}
          {draft && (
            <div className="space-y-4">
              <div className="rounded-xl bg-gray-50 border border-gray-100 p-3 text-xs text-gray-600">
                <span className="font-bold text-gray-800">نص التغريدة الأصلي: </span>{draft.original_text}
              </div>
              {draft.image_file_id && <img data-testid="import-tweet-image" src={`${API}/files/${draft.image_file_id}`} alt="" className="w-full max-h-64 object-cover rounded-xl" />}
              {field('title', 'العنوان')}
              {field('summary', 'الملخص', 2)}
              {field('content', 'نص الخبر', 6)}
              <div className="flex gap-2 justify-end pt-2">
                <button data-testid="import-tweet-draft-btn" onClick={() => publish(false)} disabled={saving} className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-bold text-gray-700 hover:bg-gray-50">حفظ كمسودة</button>
                <button data-testid="import-tweet-publish-btn" onClick={() => publish(true)} disabled={saving} className="px-5 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-bold">{saving ? 'جاري النشر...' : 'نشر الخبر'}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
